import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Edit, Trash2, Eye, EyeOff, Save, X, Upload, Video, Image as ImageIcon, Film, Share2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import api from "../../lib/api";

export default function BlogManagementPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({ 
    title: "", content: "", excerpt: "", featured_image: "", 
    images: [], videos: [], status: "draft" 
  });
  const [saving, setSaving] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await api.get("/admin/blog/posts");
      setPosts(res.data || []);
    } catch (e) { toast.error("Failed to load posts"); }
    setLoading(false);
  };

  const handleCreateNew = () => {
    setEditingPost(null);
    setFormData({ title: "", content: "", excerpt: "", featured_image: "", images: [], videos: [], status: "draft" });
    setVideoUrl("");
    setShowEditor(true);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({ 
      title: post.title, content: post.content, excerpt: post.excerpt || "", 
      featured_image: post.featured_image || "", 
      images: post.images || [], videos: post.videos || [], 
      status: post.status 
    });
    setVideoUrl("");
    setShowEditor(true);
  };

  const handleSave = async (publish) => {
    if (!formData.title || !formData.content) { toast.error("Title and content required"); return; }
    setSaving(true);
    try {
      const data = { ...formData, status: publish ? "published" : formData.status };
      if (editingPost) {
        await api.put(`/admin/blog/posts/${editingPost.id}`, data);
        toast.success("Post updated!");
      } else {
        await api.post("/admin/blog/posts", data);
        toast.success("Post created!");
      }
      setShowEditor(false);
      fetchPosts();
    } catch (e) { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/blog/posts/${id}`); toast.success("Deleted"); fetchPosts(); } 
    catch (e) { toast.error("Failed to delete"); }
  };

  const toggleStatus = async (post) => {
    try {
      await api.put(`/admin/blog/posts/${post.id}`, { status: post.status === "published" ? "draft" : "published" });
      toast.success(post.status === "published" ? "Unpublished" : "Published");
      fetchPosts();
    } catch (e) { toast.error("Failed"); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/admin/blog/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newUrl = res.data.url;
      setFormData(p => ({ 
        ...p, 
        featured_image: p.featured_image || newUrl,
        images: [...p.images, newUrl]
      }));
      toast.success("Image uploaded!");
    } catch (e) { toast.error("Upload failed"); }
    fileInputRef.current.value = "";
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video too large. Maximum size is 100MB");
      return;
    }
    
    setUploadingVideo(true);
    const fd = new FormData();
    fd.append("file", file);
    
    try {
      const res = await api.post("/admin/blog/upload-video", fd, { 
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000 // 2 minute timeout for large files
      });
      const newUrl = res.data.url;
      setFormData(p => ({ ...p, videos: [...p.videos, newUrl] }));
      toast.success(`Video uploaded! (${res.data.size_mb}MB)`);
    } catch (e) { 
      toast.error(e.response?.data?.detail || "Video upload failed"); 
    }
    setUploadingVideo(false);
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const isUploadedVideo = (url) => url?.startsWith("/api/uploads/") || url?.includes("/uploads/blog/");

  const addVideo = () => {
    if (!videoUrl.trim()) { toast.error("Please enter a video URL"); return; }
    setFormData(p => ({ ...p, videos: [...p.videos, videoUrl.trim()] }));
    setVideoUrl("");
    toast.success("Video added!");
  };

  const removeImage = (idx) => {
    setFormData(p => {
      const newImages = p.images.filter((_, i) => i !== idx);
      return { 
        ...p, 
        images: newImages,
        featured_image: p.featured_image === p.images[idx] ? (newImages[0] || "") : p.featured_image
      };
    });
  };

  const removeVideo = (idx) => {
    setFormData(p => ({ ...p, videos: p.videos.filter((_, i) => i !== idx) }));
  };

  const setAsFeatured = (url) => {
    setFormData(p => ({ ...p, featured_image: url }));
    toast.success("Set as featured image");
  };

  const getImgUrl = (url) => url?.startsWith("/") ? process.env.REACT_APP_BACKEND_URL + url : url;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "N/A";

  // Generate the public blog post URL - always use production domain
  const getBlogPostUrl = (post) => {
    const productionDomain = 'https://rtsexpress.co.uk';
    const slug = post.slug || post.id;
    return `${productionDomain}/blog/${slug}`;
  };

  // Share blog post via various platforms
  const handleShare = async (post) => {
    const postUrl = getBlogPostUrl(post);
    const shareTitle = post.title;
    const shareText = post.excerpt || `Check out this article: ${post.title}`;

    // Try native share first (mobile devices)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: postUrl
        });
        toast.success("Shared successfully!");
        return;
      } catch (err) {
        // User cancelled or native share failed, show options
        if (err.name !== 'AbortError') {
          console.log("Native share failed, showing options");
        }
      }
    }

    // Show share options modal
    showShareOptions(post, postUrl, shareTitle, shareText);
  };

  // Show share options popup
  const showShareOptions = (post, postUrl, shareTitle, shareText) => {
    const encodedUrl = encodeURIComponent(postUrl);
    const encodedTitle = encodeURIComponent(shareTitle);
    const encodedText = encodeURIComponent(shareText);

    // Create share options
    const shareOptions = [
      {
        name: 'WhatsApp',
        url: `https://wa.me/?text=${encodedText}%0A%0A${encodedUrl}`,
        color: 'bg-[#25D366]'
      },
      {
        name: 'Facebook',
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        color: 'bg-[#1877F2]'
      },
      {
        name: 'Twitter/X',
        url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
        color: 'bg-black'
      },
      {
        name: 'LinkedIn',
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        color: 'bg-[#0A66C2]'
      },
      {
        name: 'Email',
        url: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`,
        color: 'bg-slate-600'
      }
    ];

    // Create a simple modal with share options
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black/80 z-[100] flex items-center justify-center';
    modal.id = 'share-modal';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };

    modal.innerHTML = `
      <div class="bg-[#0d1117] border border-white/10 rounded-xl p-6 max-w-sm mx-4 w-full">
        <h3 class="text-lg font-bold text-white mb-4">Share: ${shareTitle.substring(0, 40)}${shareTitle.length > 40 ? '...' : ''}</h3>
        <div class="grid grid-cols-2 gap-3 mb-4">
          ${shareOptions.map(opt => `
            <a href="${opt.url}" target="_blank" rel="noopener noreferrer" 
               class="${opt.color} text-white px-4 py-2 rounded-lg text-center text-sm font-medium hover:opacity-90 transition-opacity"
               onclick="document.getElementById('share-modal').remove()">
              ${opt.name}
            </a>
          `).join('')}
        </div>
        <div class="border-t border-white/10 pt-4">
          <p class="text-slate-400 text-xs mb-2">Or copy link:</p>
          <div class="flex gap-2">
            <input type="text" value="${postUrl}" readonly 
                   class="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm" 
                   id="share-url-input" />
            <button onclick="navigator.clipboard.writeText('${postUrl}'); this.textContent='Copied!'; setTimeout(() => this.textContent='Copy', 1500);"
                    class="bg-[#22c55e] text-black px-4 py-2 rounded text-sm font-medium hover:bg-[#1ea550]">
              Copy
            </button>
          </div>
        </div>
        <button onclick="document.getElementById('share-modal').remove()" 
                class="w-full mt-4 text-slate-400 hover:text-white text-sm">
          Close
        </button>
      </div>
    `;

    document.body.appendChild(modal);
  };

  return (
    <div className="min-h-screen bg-[#0B0F14]">
      <header className="bg-[#0d1117] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-slate-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
            <div><h1 className="text-xl font-bold text-white">Blog Management</h1><p className="text-slate-400 text-sm">Create and manage blog posts</p></div>
          </div>
          <Button onClick={handleCreateNew} className="bg-[#22c55e] hover:bg-[#1ea550] text-black font-semibold"><Plus className="w-4 h-4 mr-2" />New Post</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {showEditor && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-8">
            <div className="bg-[#0d1117] border border-white/10 rounded-xl w-full max-w-3xl mx-4">
              <div className="flex justify-between p-6 border-b border-white/10">
                <h2 className="text-xl font-bold text-white">{editingPost ? "Edit Post" : "New Post"}</h2>
                <button onClick={() => setShowEditor(false)} className="text-slate-400 hover:text-white"><X className="w-6 h-6" /></button>
              </div>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <Input value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} placeholder="Title *" className="bg-slate-800 border-slate-700 text-white" />
                <Input value={formData.excerpt} onChange={(e) => setFormData(p => ({ ...p, excerpt: e.target.value }))} placeholder="Short description" className="bg-slate-800 border-slate-700 text-white" />
                <textarea value={formData.content} onChange={(e) => setFormData(p => ({ ...p, content: e.target.value }))} placeholder="Content... *" rows={8} className="w-full bg-slate-800 border border-slate-700 rounded-md px-4 py-3 text-white resize-y" />
                
                {/* Images Section */}
                <div className="border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ImageIcon className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-300">Images</span>
                  </div>
                  <input type="file" ref={fileInputRef} onChange={uploadImage} accept="image/*" className="hidden" />
                  <Button type="button" onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" className="border-slate-700 text-slate-300">
                    <Upload className="w-4 h-4 mr-2" />Upload Image
                  </Button>
                  {formData.images.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-3">
                      {formData.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img src={getImgUrl(img)} alt="" className={`w-full h-20 object-cover rounded ${formData.featured_image === img ? "ring-2 ring-[#22c55e]" : ""}`} />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                            <button onClick={() => setAsFeatured(img)} className="p-1 bg-green-500 rounded text-white text-xs" title="Set as featured">★</button>
                            <button onClick={() => removeImage(idx)} className="p-1 bg-red-500 rounded text-white text-xs" title="Remove">×</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {formData.featured_image && <p className="text-xs text-slate-500 mt-2">★ = Featured image (shown in previews)</p>}
                </div>

                {/* Videos Section */}
                <div className="border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Video className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-300">Videos</span>
                  </div>
                  
                  {/* Upload Video Button */}
                  <div className="mb-3">
                    <input 
                      type="file" 
                      ref={videoInputRef} 
                      onChange={uploadVideo} 
                      accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov" 
                      className="hidden" 
                    />
                    <Button 
                      type="button" 
                      onClick={() => videoInputRef.current?.click()} 
                      variant="outline" 
                      size="sm" 
                      className="border-slate-700 text-slate-300 bg-slate-800 hover:bg-slate-700"
                      disabled={uploadingVideo}
                    >
                      {uploadingVideo ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Film className="w-4 h-4 mr-2" />
                          Upload Video (MP4)
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 mt-1">Max 100MB. Supports MP4, WebM, MOV</p>
                  </div>
                  
                  {/* OR divider */}
                  <div className="flex items-center gap-3 my-3">
                    <div className="flex-1 border-t border-slate-700"></div>
                    <span className="text-xs text-slate-500">OR add URL</span>
                    <div className="flex-1 border-t border-slate-700"></div>
                  </div>
                  
                  {/* YouTube/Vimeo URL */}
                  <div className="flex gap-2">
                    <Input 
                      value={videoUrl} 
                      onChange={(e) => setVideoUrl(e.target.value)} 
                      placeholder="YouTube or Vimeo URL" 
                      className="bg-slate-800 border-slate-700 text-white flex-1"
                    />
                    <Button type="button" onClick={addVideo} variant="outline" size="sm" className="border-slate-700 text-slate-300">
                      <Plus className="w-4 h-4 mr-1" />Add
                    </Button>
                  </div>
                  
                  {/* Video List with Preview */}
                  {formData.videos.length > 0 && (
                    <div className="space-y-3 mt-4">
                      {formData.videos.map((video, idx) => (
                        <div key={idx} className="bg-slate-800 rounded-lg overflow-hidden">
                          {isUploadedVideo(video) ? (
                            /* HTML5 Video Preview for uploaded videos */
                            <div className="relative">
                              <video 
                                src={getImgUrl(video)} 
                                controls 
                                className="w-full h-40 bg-black"
                                preload="metadata"
                              >
                                Your browser does not support video playback.
                              </video>
                              <div className="absolute top-2 left-2 bg-green-500/90 text-white text-xs px-2 py-1 rounded">
                                Uploaded
                              </div>
                            </div>
                          ) : (
                            /* URL display for YouTube/Vimeo */
                            <div className="flex items-center gap-2 p-3">
                              <Video className="w-4 h-4 text-red-400 flex-shrink-0" />
                              <span className="text-sm text-slate-300 truncate flex-1">{video}</span>
                            </div>
                          )}
                          <div className="flex justify-end p-2 border-t border-slate-700">
                            <button 
                              onClick={() => removeVideo(idx)} 
                              className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1"
                            >
                              <X className="w-3 h-3" />Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-between p-6 border-t border-white/10">
                <select value={formData.status} onChange={(e) => setFormData(p => ({ ...p, status: e.target.value }))} className="bg-slate-800 border border-slate-700 rounded px-3 py-1 text-white">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowEditor(false)} className="border-slate-700 text-slate-300">Cancel</Button>
                  <Button onClick={() => handleSave(false)} disabled={saving} variant="outline" className="border-slate-700 text-slate-300"><Save className="w-4 h-4 mr-2" />Save Draft</Button>
                  <Button onClick={() => handleSave(true)} disabled={saving} className="bg-[#22c55e] hover:bg-[#1ea550] text-black font-semibold">Publish</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-slate-800/50 border border-white/10 rounded-xl p-4"><p className="text-slate-400 text-sm">Total</p><p className="text-2xl font-bold text-white">{posts.length}</p></div>
          <div className="bg-slate-800/50 border border-white/10 rounded-xl p-4"><p className="text-slate-400 text-sm">Published</p><p className="text-2xl font-bold text-green-400">{posts.filter(p => p.status === "published").length}</p></div>
          <div className="bg-slate-800/50 border border-white/10 rounded-xl p-4"><p className="text-slate-400 text-sm">Drafts</p><p className="text-2xl font-bold text-yellow-400">{posts.filter(p => p.status === "draft").length}</p></div>
        </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#22c55e] border-t-transparent rounded-full mx-auto"></div></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 bg-slate-800/30 rounded-xl border border-white/10">
            <p className="text-slate-400 mb-4">No blog posts yet</p>
            <Button onClick={handleCreateNew} className="bg-[#22c55e] hover:bg-[#1ea550] text-black">Create First Post</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map(post => (
              <div key={post.id} className="bg-slate-800/50 border border-white/10 rounded-xl p-5 flex justify-between items-start">
                <div className="flex gap-4 flex-1">
                  {post.featured_image && (
                    <img src={getImgUrl(post.featured_image)} alt="" className="w-20 h-20 object-cover rounded flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-lg font-semibold text-white">{post.title}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs ${post.status === "published" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>{post.status}</span>
                    </div>
                    <p className="text-slate-400 text-sm mt-1 truncate">{post.excerpt}</p>
                    <div className="flex items-center gap-4 text-slate-500 text-xs mt-2">
                      <span>{post.author} • {fmtDate(post.created_at)}</span>
                      {post.images?.length > 0 && <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" />{post.images.length}</span>}
                      {post.videos?.length > 0 && <span className="flex items-center gap-1"><Video className="w-3 h-3" />{post.videos.length}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => toggleStatus(post)} className={`p-2 rounded-lg ${post.status === "published" ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"}`} title={post.status === "published" ? "Unpublish" : "Publish"}>{post.status === "published" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  <button onClick={() => handleEdit(post)} className="p-2 bg-blue-500/20 text-blue-400 rounded-lg" title="Edit"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleShare(post)} className="p-2 bg-purple-500/20 text-purple-400 rounded-lg" title="Share" data-testid={`share-blog-${post.id}`}><Share2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(post.id)} className="p-2 bg-red-500/20 text-red-400 rounded-lg" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
